import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaTimes, FaCheck, FaSpinner, FaInfo, FaImage, FaTrash, FaUpload } from 'react-icons/fa';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const QuestionModal = ({ isOpen, onClose, testId, questionId = null, onSuccess }) => {
  const [question, setQuestion] = useState({
    question_text: '',
    option_1: '',
    option_2: '',
    option_3: '',
    option_4: '',
    correct_option: 'option_1',
    marks: 1
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [currentField, setCurrentField] = useState('question_text');
  
  // Media state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaTypes, setMediaTypes] = useState({});
  const [mediaPreview, setMediaPreview] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // References
  const questionTextRef = useRef(null);
  const option1Ref = useRef(null);
  const option2Ref = useRef(null);
  const option3Ref = useRef(null);
  const option4Ref = useRef(null);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Load MathJax script
  useEffect(() => {
    if (!isOpen) return;
    
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.9/MathJax.js?config=TeX-MML-AM_CHTML';
    script.async = true;
    
    // Check if script already exists
    if (!document.querySelector(`script[src="${script.src}"]`)) {
      document.head.appendChild(script);
      
      script.onload = () => {
        // Configure MathJax
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
            processEscapes: true
          },
          "HTML-CSS": { availableFonts: ["TeX"] }
        });
      };
    }
  }, [isOpen]);
  
  // Function to render math expressions
  const renderMath = () => {
    if (window.MathJax && previewRef.current) {
      window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, previewRef.current]);
    }
  };
  
  // Render math when content changes
  useEffect(() => {
    renderMath();
  }, [question.question_text, question.option_1, question.option_2, question.option_3, question.option_4]);
  
  // Fetch question data if in edit mode
  useEffect(() => {
    if (!isOpen) return;
    
    // Reset form when modal opens
    if (!questionId) {
      setQuestion({
        question_text: '',
        option_1: '',
        option_2: '',
        option_3: '',
        option_4: '',
        correct_option: 'option_1',
        marks: 1
      });
      setMediaFiles([]);
      setMediaTypes({});
      setMediaPreview({});
      setIsEdit(false);
      return;
    }
    
    // Fetch question data for editing
    const fetchQuestion = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/Questions/${questionId}`);
        
        // If marks field doesn't exist in the data, default to 1
        const questionData = {
          ...response.data,
          marks: response.data.marks || 1
        };
        
        setQuestion(questionData);
        
        // Set media previews if available
        if (questionData.media) {
          const previews = {};
          for (const [mediaType, url] of Object.entries(questionData.media)) {
            previews[mediaType] = url;
          }
          setMediaPreview(previews);
        }
        
        setIsEdit(true);
      } catch (err) {
        setError('Failed to load question data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuestion();
  }, [isOpen, questionId]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuestion({
      ...question,
      [name]: value
    });
  };
  
  // Handle number input for marks
  const handleMarksChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setQuestion({
      ...question,
      marks: isNaN(value) ? 1 : Math.max(1, value) // Ensure minimum value is 1
    });
  };
  
  // Handle correct option selection
  const handleCorrectOptionChange = (e) => {
    setQuestion({
      ...question,
      correct_option: e.target.value
    });
  };
  
  // Get current field ref
  const getCurrentFieldRef = () => {
    switch (currentField) {
      case 'question_text':
        return questionTextRef;
      case 'option_1':
        return option1Ref;
      case 'option_2':
        return option2Ref;
      case 'option_3':
        return option3Ref;
      case 'option_4':
        return option4Ref;
      default:
        return questionTextRef;
    }
  };
  
  // Insert text at cursor position
  const insertTextAtCursor = (text) => {
    const textareaRef = getCurrentFieldRef();
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = question[currentField].substring(0, start);
    const textAfter = question[currentField].substring(end);
    
    setQuestion({
      ...question,
      [currentField]: textBefore + text + textAfter
    });
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };
  
  // Handle file input change
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setMediaFiles([...mediaFiles, ...files]);
    
    // Set media type for the new files
    const newMediaTypes = { ...mediaTypes };
    files.forEach((file, index) => {
      const fileIndex = mediaFiles.length + index;
      newMediaTypes[fileIndex] = currentField;
    });
    setMediaTypes(newMediaTypes);
    
    // Generate preview URLs
    files.forEach((file, index) => {
      const fileIndex = mediaFiles.length + index;
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(prev => ({
          ...prev,
          [currentField]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the file input
    e.target.value = '';
  };
  
  // Upload media button
  const handleUploadClick = (mediaType) => {
    setCurrentField(mediaType);
    fileInputRef.current.click();
  };
  
  // Remove media
  const handleRemoveMedia = (mediaType) => {
    // Update mediaTypes to remove references to this media type
    const newMediaTypes = { ...mediaTypes };
    Object.keys(newMediaTypes).forEach(key => {
      if (newMediaTypes[key] === mediaType) {
        delete newMediaTypes[key];
      }
    });
    setMediaTypes(newMediaTypes);
    
    // Remove preview
    setMediaPreview(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[mediaType];
      return newPreviews;
    });
    
    // If in edit mode, we'll need to track that this media should be removed
    if (isEdit && question.media && question.media[mediaType]) {
      setQuestion(prev => ({
        ...prev,
        media: {
          ...prev.media,
          [mediaType]: null
        }
      }));
    }
  };
  
  // Formatting functions
  const formatBold = () => insertTextAtCursor('<strong>text</strong>');
  const formatItalic = () => insertTextAtCursor('<em>text</em>');
  const formatUnderline = () => insertTextAtCursor('<u>text</u>');
  const formatOrderedList = () => insertTextAtCursor('<ol>\\n<li>Item 1</li>\\n<li>Item 2</li>\\n</ol>');
  const formatUnorderedList = () => insertTextAtCursor('<ul>\\n<li>Item 1</li>\\n<li>Item 2</li>\\n</ul>');
  const formatRemove = () => insertTextAtCursor('');
  
  // Math formatting functions
  const formatXSquared = () => insertTextAtCursor('$x^2$');
  const formatXPowerN = () => insertTextAtCursor('$x^n$');
  const formatSqrtX = () => insertTextAtCursor('$\\sqrt{x}$');
  const formatSinSquared = () => insertTextAtCursor('$\\sin^2(x)$');
  const formatCosSquared = () => insertTextAtCursor('$\\cos^2(x)$');
  const formatTanSquared = () => insertTextAtCursor('$\\tan^2(x)$');
  const formatSecSquared = () => insertTextAtCursor('$\\sec^2(x)$');
  const formatCosecSquared = () => insertTextAtCursor('$\\csc^2(x)$');
  const formatCotSquared = () => insertTextAtCursor('$\\cot^2(x)$');
  
  // Save question with media
  const saveQuestion = async () => {
    // Validate inputs - check both text and media content
    const isFieldValid = (field) => {
      // Field is valid if it has text content OR has media
      return !!question[field] || !!mediaFiles.find((_file, index) => mediaTypes[index] === field);
    };
    
    if (!isFieldValid('question_text') || !isFieldValid('option_1') || !isFieldValid('option_2')) {
      setError('Question text and at least two options are required (either as text or images).');
      return;
    }
    
    try {
      setLoading(true);
      setUploadProgress(0);
      setError(null);
      
      let savedQuestion;
      
      // If editing with no media files, use simple update
      if (isEdit && mediaFiles.length === 0) {
        const response = await axios.put(`${API_BASE_URL}/Questions/${questionId}`, {
          ...question,
          updated_at: new Date()
        });
        savedQuestion = response.data;
      } else {
        // Prepare to upload media files
        const mediaUrls = {};
        
        // Upload media files
        for (const [index, file] of mediaFiles.entries()) {
          const mediaType = mediaTypes[index];
          
          // Create form data for single file upload
          const formData = new FormData();
          formData.append('image', file);
          
          try {
            // Upload individual file
            const response = await axios.post(`${API_BASE_URL}/questions/upload-image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
              }
            });
            
            // Store the URL with its media type
            if (response.data && response.data.imageUrl) {
              mediaUrls[mediaType] = response.data.imageUrl;
            } else {
              throw new Error(`No image URL returned for ${mediaType}`);
            }
          } catch (uploadError) {
            console.error(`Error uploading image for ${mediaType}:`, uploadError);
            setError(`Failed to upload image for ${mediaType}: ${uploadError.message}`);
            setLoading(false);
            return;
          }
        }
        
        // Prepare question data with media URLs
        const questionToSave = { ...question };
        
        // Add media info as a proper structure
        questionToSave.media = {};
        
        // Map fields to their media URLs
        Object.entries(mediaUrls).forEach(([fieldType, url]) => {
          // Store the URL in the media object
          questionToSave.media[fieldType] = url;
          
          // Create img tag in the content field to display the image
          if (!questionToSave[fieldType] || questionToSave[fieldType].trim() === '') {
            questionToSave[fieldType] = `<img src="${url}" alt="${fieldType}" class="question-image" />`;
          }
        });
        
        // Add test ID if creating new question
        if (!isEdit && testId) {
          questionToSave.testId = testId;
        }
        
        // Send request to create question
        const response = await axios.post(`${API_BASE_URL}/Questions`, questionToSave);
        savedQuestion = response.data;
        
        // Associate the question with the test if this is a new question
        if (!isEdit && testId) {
          await axios.post(`${API_BASE_URL}/QuestionTestAssociations/test/${testId}/add-questions`, {
            question_ids: [savedQuestion.question_id]
          });
        }
      }
      
      // Notify parent component of success
      if (onSuccess) {
        onSuccess(savedQuestion);
      }
      
      // Close modal
      onClose();
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} question. Please try again.`);
      console.error('Error saving question:', err);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  // Formatting toolbar buttons
  const renderFormattingToolbar = () => {
    return (
      <div className="flex flex-wrap gap-1 mb-2">
        <button type="button" onClick={formatBold} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">B</button>
        <button type="button" onClick={formatItalic} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">I</button>
        <button type="button" onClick={formatUnderline} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">U</button>
        <button type="button" onClick={formatOrderedList} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">OL</button>
        <button type="button" onClick={formatUnorderedList} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">UL</button>
        <button type="button" onClick={formatRemove} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">Remove</button>
        <button type="button" onClick={formatXSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">x²</button>
        <button type="button" onClick={formatXPowerN} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">xⁿ</button>
        <button type="button" onClick={formatSqrtX} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">√x</button>
        <button type="button" onClick={formatSinSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">sin²</button>
        <button type="button" onClick={formatCosSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">cos²</button>
        <button type="button" onClick={formatTanSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">tan²</button>
        <button type="button" onClick={formatSecSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">sec²</button>
        <button type="button" onClick={formatCosecSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">cosec²</button>
        <button type="button" onClick={formatCotSquared} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">cot²</button>
      </div>
    );
  };
  
  // Media upload button
  const renderMediaButton = (fieldName) => {
    return (
      <div className="flex items-center space-x-2">
        <button 
          type="button" 
          className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 mb-2 flex items-center"
          onClick={() => handleUploadClick(fieldName)}
        >
          <FaImage className="mr-2" /> Upload Media
        </button>
        
        {mediaPreview[fieldName] && (
          <button
            type="button"
            onClick={() => handleRemoveMedia(fieldName)}
            className="px-2 py-2 bg-red-500 text-white rounded hover:bg-red-600 mb-2"
            title="Remove media"
          >
            <FaTrash />
          </button>
        )}
      </div>
    );
  };
  
  // Media preview
  const renderMediaPreview = (fieldName) => {
    if (!mediaPreview[fieldName]) return null;
    
    return (
      <div className="mb-2 border p-2 rounded bg-gray-50">
        <p className="text-sm text-gray-500 mb-1">Media Preview:</p>
        <img 
          src={mediaPreview[fieldName]} 
          alt={`Preview for ${fieldName}`} 
          className="max-h-40 max-w-full object-contain" 
        />
      </div>
    );
  };
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-100 sticky top-0 z-10">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEdit ? 'Edit Question' : 'Add New Question'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FaTimes size={20} />
          </button>
        </div>
        
        {/* Preview Section - Fixed at Top */}
        <div className="px-6 py-4 border-b bg-gray-50 sticky top-14 z-10">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium text-gray-800">Preview</h3>
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-600 mr-2">Marks:</span>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {question.marks}
              </span>
            </div>
          </div>
          
          <div ref={previewRef} className="space-y-4 bg-white border rounded-md p-4 shadow-sm">
            {/* Question Preview */}
            <div>
              <div className="font-medium text-gray-700 mb-2">Question</div>
              <div className="p-3 bg-gray-50 border rounded-md">
                {question.question_text ? (
                  <div dangerouslySetInnerHTML={{ __html: question.question_text }} />
                ) : (
                  <p className="text-gray-400 italic">Question text will appear here</p>
                )}
              </div>
              
              {/* Question Media Preview */}
              {mediaPreview['question_text'] && (
                <div className="mt-2">
                  <img 
                    src={mediaPreview['question_text']} 
                    alt="Question media" 
                    className="max-h-40 object-contain border rounded" 
                  />
                </div>
              )}
            </div>
            
            {/* Options Preview */}
            <div>
              <div className="font-medium text-gray-700 mb-2">Options</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {['option_1', 'option_2', 'option_3', 'option_4'].map((option, index) => (
                  question[option] && (
                    <div 
                      key={option}
                      className={`p-3 rounded-md border ${
                        question.correct_option === option 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <input 
                        type="radio" 
                        checked={question.correct_option === option}
                        readOnly
                        className="mr-2"
                      />
                      <span dangerouslySetInnerHTML={{ __html: question[option] }} />
                      
                      {/* Option Media Preview */}
                      {mediaPreview[option] && (
                        <div className="mt-2">
                          <img 
                            src={mediaPreview[option]} 
                            alt={`Option ${index + 1} media`} 
                            className="max-h-32 object-contain border rounded" 
                          />
                        </div>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal Body - Scrollable Editor */}
        <div className="p-6 overflow-y-auto flex-grow">
          {loading && !question.question_text ? (
            <div className="flex justify-center items-center h-40">
              <FaSpinner className="animate-spin text-3xl text-gray-500" />
            </div>
          ) : (
            <div>
              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 mb-6">
                  {error}
                </div>
              )}
              
              {/* Upload progress bar */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Uploading: {uploadProgress}%</p>
                </div>
              )}
              
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
              />
              
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-800">Editor</h3>
                
                {/* Marks Input */}
                <div className="flex items-center">
                  <label htmlFor="marks" className="mr-2 text-sm font-medium text-gray-700">
                    Marks:
                  </label>
                  <input
                    type="number"
                    id="marks"
                    name="marks"
                    min="1"
                    value={question.marks}
                    onChange={handleMarksChange}
                    className="w-16 px-2 py-1 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Question Input */}
              <div className="mb-6">
                <div className="mb-2">Question</div>
                
                {/* Media upload and preview */}
                {renderMediaButton('question_text')}
                {renderMediaPreview('question_text')}
                
                {renderFormattingToolbar()}
                <textarea
                  ref={questionTextRef}
                  name="question_text"
                  value={question.question_text}
                  onChange={handleInputChange}
                  onFocus={() => setCurrentField('question_text')}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your question here"
                ></textarea>
              </div>
              
              {/* Options */}
              <div className="mb-6">
                <div className="mb-2">Answers</div>
                
                {['option_1', 'option_2', 'option_3', 'option_4'].map((option, index) => (
                  <div key={option} className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="radio"
                        id={`correct_option_${index + 1}`}
                        name="correct_option"
                        value={option}
                        checked={question.correct_option === option}
                        onChange={handleCorrectOptionChange}
                        className="mr-2"
                      />
                      <label htmlFor={`correct_option_${index + 1}`} className="text-sm font-medium text-gray-700">
                        Option {index + 1} {question.correct_option === option && '(Correct)'}
                      </label>
                    </div>
                    
                    {/* Media upload and preview */}
                    {renderMediaButton(option)}
                    {renderMediaPreview(option)}
                    
                    {renderFormattingToolbar()}
                    
                    <textarea
                      ref={[option1Ref, option2Ref, option3Ref, option4Ref][index]}
                      id={option}
                      name={option}
                      value={question[option] || ''}
                      onChange={handleInputChange}
                      onFocus={() => setCurrentField(option)}
                      rows="2"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        question.correct_option === option
                          ? 'border-green-300 focus:ring-green-500 bg-green-50'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder={`Option ${index + 1}`}
                    ></textarea>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Modal Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={saveQuestion}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaCheck className="mr-2" />
                {isEdit ? 'Update Question' : 'Create Question'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionModal;